<template>
  <div class="section">
    <div class="container">
      <div class="level">
        <Header class="level-left" v-bind="$route.meta" />
        <b-button
          class="left-right"
          type="is-success"
          icon-left="plus"
          :disabled="selectedPlant === null || isLoading || allSites"
          @click="showCreateForm()"
        >
          New Accident
        </b-button>
      </div>
      <div class="columns">
        <div class="column">
          <b-field grouped>
            <b-field label="Start Date">
              <b-datepicker
                v-model="start"
                name="startdate"
                :first-day-of-week="1"
                :locale="'en-CA'"
                icon="calendar-alt"
                placeholder="Start date"
              />
            </b-field>
            <b-field label="End Date">
              <b-datepicker
                v-model="end"
                name="enddate"
                :first-day-of-week="1"
                :locale="'en-CA'"
                icon="calendar-alt"
                placeholder="End date"
              />
            </b-field>
            <b-field v-if="hasGroupPlants" label="Plant">
              <b-select v-model="selectedPlant" icon="industry" :disabled="allSites" expanded>
                <option v-for="plant in groupPlants" :key="plant.id" :value="plant">
                  {{ plant.name }}
                </option>
              </b-select>
            </b-field>
            <b-field v-else label="Plant">
              <multiselect
                v-model="selectedPlant"
                :options="plants"
                :multiple="false"
                :close-on-select="true"
                :disabled="allSites"
                placeholder="Pick plant"
                :custom-label="(obj) => obj.id + ' - ' + obj.name"
                track-by="name"
                style="min-width: 400px;"
              />
            </b-field>
            <b-field v-if="canViewAllSites" label="All Sites">
              <b-switch v-model="allSites" @input="resetData()">
                Every site
              </b-switch>
            </b-field>
          </b-field>
          <b-field>
            <b-button
              class="left-right"
              type="is-warning"
              icon-left="arrows-rotate"
              :disabled="isLoading || (!allSites && selectedPlant === null)"
              @click="() => {resetData(); getData();}"
            >
              Load
            </b-button>
          </b-field>
          <ag-grid-vue
            v-if="accident.length != 0"
            style="width: 100%; height: 700px;"
            class="ag-theme-balham"
            :row-data="accident"
            :grid-options="gridOptions"
            :column-defs="columnDefs"
            :pagination="true"
            :pagination-auto-page-size="true"
            :cache-overflow-size="0"
            :multi-sort-key="'ctrl'"
            :modules="modules"
            :get-context-menu-items="getContextMenuItems"
          />
        </div>
      </div>
    </div>
    <b-modal :active.sync="isCreatingAccident" scroll="keep" has-modal-card>
      <form v-if="isCreatingAccident" @submit.prevent="submitAccident">
        <div class="modal-card" style="min-width: 50vw; width: fit-content; min-height: 50vh; height: fit-content;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Create Accident
              <span v-if="selectedPlant" style="font-size:1rem; text-overflow: elipsis">{{ selectedPlant.name }}</span>
            </p>
          </header>
          <section class="modal-card-body">
            <b-field grouped>
              <b-field label="Date">
                <b-datepicker
                  v-model="newAccident.date"
                  name="newdate"
                  :first-day-of-week="1"
                  :locale="'en-CA'"
                  icon="calendar-alt"
                />
              </b-field>
            </b-field>
            <b-field label="Shift">
              <b-select v-model="newAccident.shift">
                <option value="AM">
                  AM
                </option>
                <option value="PM">
                  PM
                </option>
                <option value="Night">
                  Night
                </option>
              </b-select>
            </b-field>
            <b-field label="Reporter">
              <b-select v-model="newAccident.reporter_id">
                <option value="">
                  None
                </option>
                <option v-for="employee in employees" :key="'new' + employee.id" :value="employee.id">
                  {{ employee.name + ' ' + employee.surname }}
                </option>
              </b-select>
            </b-field>
            <b-field label="Involved">
              <multiselect
                v-model="newAccident.involved_id"
                :options="employees"
                :multiple="true"
                :close-on-select="false"
                placeholder="Select Employees"
                :custom-label="(obj) => obj.name + ' ' + obj.surname"
                track-by="name"
                style="min-width: 400px;"
              />
            </b-field>
            <b-field label="Type">
              <b-select v-model="newAccident.type">
                <option value="dangerous situation">
                  dangerous situation
                </option>
                <option value="near miss">
                  near miss
                </option>
                <option value="minor accident">
                  minor accident
                </option>
                <option value="AT WLT">
                  AT WLT
                </option>
                <option value="AT LT">
                  AT LT
                </option>
              </b-select>
            </b-field>
            <b-field label="Arret">
              <b-input v-model="newAccident.arret" type="number" />
            </b-field>
            <b-field label="Comment">
              <b-input v-model="newAccident.comment" type="text" />
            </b-field>
          </section>
          <footer class="modal-card-foot">
            <button type="submit" class="button is-info is-fullwidth" :class="{ 'is-loading': isSaving }" :disabled="isSaving">
              {{ $t('global.submit') }}
            </button>
          </footer>
        </div>
      </form>
    </b-modal>
    <b-modal :active.sync="isEditingAccident" scroll="keep" has-modal-card>
      <form v-if="isEditingAccident" @submit.prevent="submitEdit">
        <div class="modal-card" style="min-width: 50vw; width: fit-content; min-height: 50vh; height: fit-content;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Edit Accident
              <span v-if="allSites" style="font-size:1rem; text-overflow: elipsis">{{ editableAccident.plantName }}</span>
              <span v-else-if="selectedPlant" style="font-size:1rem; text-overflow: elipsis">{{ selectedPlant.name }}</span>
            </p>
          </header>
          <section class="modal-card-body">
            <b-field grouped>
              <b-field label="Date">
                <b-datepicker
                  v-model="editableAccident.date"
                  name="newdate"
                  :first-day-of-week="1"
                  :locale="'en-CA'"
                  icon="calendar-alt"
                />
              </b-field>
            </b-field>
            <b-field label="Shift">
              <b-select v-model="editableAccident.shift">
                <option value="AM">
                  AM
                </option>
                <option value="PM">
                  PM
                </option>
                <option value="Night">
                  Night
                </option>
              </b-select>
            </b-field>
            <b-field label="Reporter">
              <b-select v-model="editableAccident.reporter_id">
                <option value="">
                  None
                </option>
                <option v-for="employee in editableAccidentEmployees" :key="'edit' + employee.id" :value="employee.id">
                  {{ employee.name + ' ' + employee.surname }}
                </option>
              </b-select>
            </b-field>
            <b-field label="Involved">
              <multiselect
                v-model="editableAccident.involved_id"
                :options="editableAccidentEmployees"
                :multiple="true"
                :close-on-select="false"
                placeholder="Select Employees"
                :custom-label="(obj) => obj.name + ' ' + obj.surname"
                track-by="name"
                style="min-width: 400px;"
              />
            </b-field>
            <b-field label="Type">
              <b-select v-model="editableAccident.type">
                <option value="dangerous situation">
                  dangerous situation
                </option>
                <option value="near miss">
                  near miss
                </option>
                <option value="minor accident">
                  minor accident
                </option>
                <option value="AT WLT">
                  AT WLT
                </option>
                <option value="AT LT">
                  AT LT
                </option>
              </b-select>
            </b-field>
            <b-field label="Arret">
              <b-input v-model="editableAccident.arret" type="number" />
            </b-field>
            <b-field label="Comment">
              <b-input v-model="editableAccident.comment" type="text" />
            </b-field>
          </section>
          <footer class="modal-card-foot">
            <button type="submit" class="button is-info is-fullwidth">
              {{ $t('global.submit') }}
            </button>
          </footer>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script>
import { AllModules } from '@ag-grid-enterprise/all-modules'
import { mapState } from 'vuex'
import Accident from '../../services/v3/accident'
import EmployeeManagement from '../../services/v3/employeeManagement'
import plantSelector from '@/mixins/plantSelector'
import saving from '@/mixins/saving'
import { today } from '@/helpers/dates'
import { restoreConsultingPlant } from '@/helpers/plants'
// import sortAndFilterMixins from '@/mixins/agGridSortAndFilter'

export default {
  name: 'Accident',
  components: {
  },
  mixins: [plantSelector, saving],
  data: function () {
    return {
      editableAccident: null,
      newAccident: null,
      selectedPlant: restoreConsultingPlant(),
      isLoading: false,
      start: null,
      end: new Date(),
      isCreatingAccident: false,
      isEditingAccident: false,
      allSites: false,
      accident: [],
      employees: [],
      modules: AllModules,
      loadedEmployees: '',
      gridOptions: {
        cacheBlockSize: 20,
        paginationPageSize: 20,
        enableRangeSelection: true,
        defaultColDef: {
          floatingFilter: true,
          enableValue: true,
          enableRowGroup: true,
          enablePivot: false,
          resizable: true
        },
        sideBar: {
          toolPanels: [
            {
              id: 'columns',
              labelDefault: 'Columns',
              labelKey: 'columns',
              iconKey: 'columns',
              toolPanel: 'agColumnsToolPanel',
              toolPanelParams: {
                // tool panel columns won't move when columns are reordered in the grid
                suppressSyncLayoutWithGrid: true,
                // prevents columns being reordered from the Columns Tool Panel
                suppressColumnMove: true
              }
            }
          ],
          defaultToolPanel: ''
        }
      }
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user
    }),
    canViewAllSites () {
      const rights = this.user?.role?.rights?.map(e => e.name) || []
      return rights.includes('Production Bonus Management')
    },
    // Site names are only worth a column when rows can come from more than one site.
    columnDefs () {
      const plantColumn = {
        headerName: 'Plant',
        field: 'plantName',
        filter: 'agTextColumnFilter',
        width: 220,
        sortable: true,
        filterParams: { suppressAndOrCondition: true }
      }
      const baseColumns = [
        { headerName: 'Date',
          field: 'date',
          filter: 'agTextColumnFilter',
          width: 160,
          sortable: true,
          filterParams: { suppressAndOrCondition: true }
        },
        { headerName: 'Shift',
          field: 'shift',
          filter: 'agTextColumnFilter',
          width: 160,
          sortable: true,
          filterParams: { suppressAndOrCondition: true }
        },
        { headerName: 'Reporter',
          field: 'reporter_id',
          filter: 'agTextColumnFilter',
          width: 160,
          sortable: true,
          valueFormatter: (params) => {
            return this.employees.filter(x => x.id === parseInt(params.value)).map(x => x.name + ' ' + x.surname + (x.title ? ' (' + x.title + ')' : ''))
          },
          filterParams: { suppressAndOrCondition: true }
        },
        { headerName: 'Involved',
          field: 'involved_id',
          filter: 'agTextColumnFilter',
          width: 160,
          sortable: true,
          valueFormatter: (params) => {
            return params.value.map(x => x.name + ' ' + x.surname + (x.title ? ' (' + x.title + ')' : '') + ', ').reduce((sum, element) => sum + element, '')
          },
          filterParams: { suppressAndOrCondition: true }
        },
        { headerName: 'Comment',
          field: 'comment',
          filter: 'agTextColumnFilter',
          width: 160,
          sortable: true,
          filterParams: { suppressAndOrCondition: true }
        },
        { headerName: 'Type',
          field: 'type',
          filter: 'agTextColumnFilter',
          width: 160,
          sortable: true,
          filterParams: { suppressAndOrCondition: true }
        },
        { headerName: 'Leave Days',
          field: 'arret',
          filter: 'agTextColumnFilter',
          width: 160,
          sortable: true,
          filterParams: { suppressAndOrCondition: true }
        }
      ]
      return this.allSites ? [plantColumn, ...baseColumns] : baseColumns
    },
    // In all-sites mode this.employees spans several sites, so the edit form has to
    // stay scoped to the row's own site to avoid assigning someone else's employee.
    editableAccidentEmployees () {
      if (!this.allSites || !this.editableAccident) {
        return this.employees
      }
      return this.employees.filter(x => String(x.id_site) === String(this.editableAccident.id_site))
    }
  },
  mounted: async function () {
    this.start = new Date()
    this.start.setDate(new Date().getDate() - 30)
    // All-sites mode names every plant in the grid, so it needs the full list
    // even when the group is scoped to its own sites.
    await this.loadPlants({ force: this.canViewAllSites })
  },
  methods: {
    resetData () {
      this.accident = []
    },
    // Reporter and involved names are resolved client-side against this.employees,
    // so the cache key tracks which sites were loaded, not just a single plant.
    async loadEmployees (siteIds, cacheKey) {
      if (this.loadedEmployees === cacheKey) {
        return
      }
      const perSite = await Promise.all(siteIds.map(siteId => EmployeeManagement.getTable({ siteId })))
      this.employees = []
      perSite.forEach(employees => {
        employees.forEach(element => {
          this.employees.push({
            id: element.id,
            id_site: element.id_site,
            name: element.name,
            matricule: element.matricule,
            title: element.title,
            surname: element.surname,
            date_entree: element.date_entree ? new Date(element.date_entree.split('Z')[0]).toLocaleDateString('en-CA') : null,
            date_sortie: element.date_sortie ? new Date(element.date_sortie.split('Z')[0]).toLocaleDateString('en-CA') : null
          })
        })
      })
      this.loadedEmployees = cacheKey
    },
    async loadSelectedPlantEmployees () {
      if (!this.selectedPlant || !this.selectedPlant.id) {
        return
      }
      await this.loadEmployees([this.selectedPlant.id], String(this.selectedPlant.id))
    },
    async showCreateForm () {
      this.isLoading = true
      await this.loadSelectedPlantEmployees()
      this.newAccident = {
        id_site: this.selectedPlant.id,
        involved_id: [],
        reporter_id: '',
        date: today(),
        shift: 'AM',
        arret: 0,
        type: 'dangerous situation',
        comment: ''
      }
      this.isLoading = false
      this.isCreatingAccident = true
    },
    async showEditForm (accident) {
      this.isLoading = true
      // In all-sites mode every site in the result set is already loaded; refetching
      // for the selected plant would drop the names the other sites' rows rely on.
      if (!this.allSites) {
        await this.loadSelectedPlantEmployees()
      }
      this.editableAccident = accident
      this.isLoading = false
      this.isEditingAccident = true
    },
    async getData () {
      if (!this.allSites && (!this.selectedPlant || !this.selectedPlant.id)) {
        this.$buefy.toast.open({ message: 'Please select a plant', duration: 3000, type: 'is-danger' })
        return
      }
      if (!this.allSites) {
        sessionStorage.setItem('consultingPlant', JSON.stringify(this.selectedPlant))
      }
      this.isLoading = true
      try {
        let data
        if (this.allSites) {
          data = await Accident.getAllTable({
            start: this.start.toLocaleDateString('en-CA'),
            end: this.end.toLocaleDateString('en-CA')
          })
          // Only fetch employees for the sites that actually appear in the results.
          const siteIds = [...new Set(data.map(element => element.id_site))]
          await this.loadEmployees(siteIds, 'all:' + siteIds.slice().sort().join(','))
        } else {
          await this.loadSelectedPlantEmployees()
          data = await Accident.getTable({
            siteId: this.selectedPlant.id,
            start: this.start.toLocaleDateString('en-CA'),
            end: this.end.toLocaleDateString('en-CA')
          })
        }
        data.forEach(element => {
          const involvedIds = element.involved_id ? JSON.parse(element.involved_id) : []
          this.accident.push({
            id: element.id,
            id_site: element.id_site,
            plantName: this.plantNamesById[String(element.id_site)] || element.id_site,
            involved_id: this.employees.filter(x => {
              return involvedIds.includes(x.id)
            }),
            reporter_id: element.reporter_id,
            date: element.date ? new Date(element.date.split('Z')[0]).toLocaleDateString('en-CA') : null,
            shift: element.shift,
            arret: element.arret,
            type: element.type,
            comment: element.comment
          })
        })
        if (this.accident.length > 0) {
          this.$buefy.toast.open({ message: 'Data loaded', duration: 3000, type: 'is-success' })
        } else {
          this.$buefy.toast.open({ message: 'No corresponding Accident found', duration: 3000, type: 'is-danger' })
        }
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
        this.isLoading = false
      }
      this.isLoading = false
    },
    getContextMenuItems (params) {
      const selectedAccident = {
        date: new Date(new Date(params.node.data.date).toISOString().split('T')[0] + 'T00:00:00'),
        id: params.node.data.id,
        id_site: params.node.data.id_site,
        plantName: params.node.data.plantName,
        involved_id: params.node.data.involved_id,
        reporter_id: params.node.data.reporter_id,
        shift: params.node.data.shift,
        arret: params.node.data.arret,
        type: params.node.data.type,
        comment: params.node.data.comment
      }
      let customMenu = []
      const genericMenu = [
        'separator',
        'copy',
        'copyWithHeaders',
        'separator',
        'export'
      ]
      customMenu.push({
        name: 'Edit Accident',
        action: () => {
          this.showEditForm(selectedAccident)
        },
        icon: '<i class="fas fa-fw fa-edit" ></i>'
      })
      customMenu.push({
        name: 'Delete Accident',
        action: () => {
          this.deleteAccident(selectedAccident)
        },
        icon: '<i class="fas fa-fw fa-xmark" ></i>'
      })
      const result = [...customMenu, ...genericMenu]
      return result
    },
    async submitAccident () {
      if (this.newAccident.involved_id.length === 0 && this.newAccident.reporter_id === '') {
        this.$buefy.toast.open({ message: 'One of reporter or involved is required', duration: 3000, type: 'is-danger' })
        return
      }
      const saved = await this.runSave(() => Accident.createAccident({
        id_site: this.newAccident.id_site,
        involved_id: JSON.stringify(this.newAccident.involved_id.map(x => x.id)),
        reporter_id: this.newAccident.reporter_id,
        date: this.newAccident.date ? new Date(this.newAccident.date).toLocaleDateString('en-CA') : null,
        shift: this.newAccident.shift,
        arret: this.newAccident.arret,
        type: this.newAccident.type,
        comment: this.newAccident.comment
      }))
      if (!saved) {
        return
      }
      this.isCreatingAccident = false
      this.$buefy.toast.open({ message: 'Accident Created', duration: 3000, type: 'is-success' })
      this.resetData()
      this.getData()
    },
    async submitEdit () {
      if (this.editableAccident.involved_id.length === 0 && this.editableAccident.reporter_id === '') {
        this.$buefy.toast.open({ message: 'One of reporter or involved is required', duration: 3000, type: 'is-danger' })
        return
      }
      this.isEditingAccident = false
      try {
        await Accident.updateAccident({
          id: this.editableAccident.id,
          id_site: this.editableAccident.id_site,
          involved_id: JSON.stringify(this.editableAccident.involved_id.map(x => x.id)),
          reporter_id: this.editableAccident.reporter_id,
          date: this.editableAccident.date ? new Date(this.editableAccident.date).toLocaleDateString('en-CA') : null,
          shift: this.editableAccident.shift,
          arret: this.editableAccident.arret,
          type: this.editableAccident.type,
          comment: this.editableAccident.comment
        })
        this.$buefy.toast.open({ message: 'Accident Updated', duration: 3000, type: 'is-success' })
        this.resetData()
        this.getData()
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
      }
    },
    async deleteAccident (accident) {
      try {
        await Accident.archiveAccident(accident.id)
        this.$buefy.toast.open({ message: 'Accident Deleted', duration: 3000, type: 'is-warning' })
        this.resetData()
        this.getData()
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
      }
    }
  }
}

</script>
